/* global google */
import { __awaiter } from "tslib";
/**
 * VAST/VPAID, IMA Api script
 * https://developers.google.com/interactive-media-ads/docs/sdks/html5/
 */
import { debug, loadScriptTag } from 'shared/utils';
import VideoBase from './videoBase';
class VideoIMA extends VideoBase {
    constructor() {
        super(...arguments);
        this.videoIsStarted = false;
        this.methodBounds = false;
        this.currentTime = 0;
        this.pluginSetup = () => __awaiter(this, void 0, void 0, function* () {
            yield preSetupIMA();
            return new Promise((resolve, reject) => {
                google.ima.settings.setVpaidMode(google.ima.ImaSdkSettings.VpaidMode.ENABLED);
                // For VAST/HLS, requires video content <video>
                // our original video element now becomes ad display container
                const adContent = this.videoElement;
                // setup display container for the ad
                const adDisplayContainer = new google.ima.AdDisplayContainer(adContent, this.videoPlaceholder);
                adDisplayContainer.initialize();
                this.videoPlaceholder.load();
                this.videoPlaceholder.onended = () => {
                    this.adsLoader.contentComplete();
                };
                // Create ads loader
                this.adsLoader = new google.ima.AdsLoader(adDisplayContainer);
                this.adsLoader.addEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, (e) => {
                    this.onAdsManagerLoaded(e);
                    resolve();
                }, false);
                this.adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, (e) => {
                    this.onVastError(e);
                    reject();
                }, false);
                // Create request video ads.
                // adTagUrl format: https://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/124319096/external/single_ad_samples&ciu_szs=300x250..
                const adsRequest = new google.ima.AdsRequest();
                adsRequest.adTagUrl = this.config.videoUrl;
                // Specify the linear and nonlinear slot sizes. This helps the SDK to
                // select the correct creative if multiple are returned.
                adsRequest.linearAdSlotWidth = 640;
                adsRequest.linearAdSlotHeight = 400;
                adsRequest.nonLinearAdSlotWidth = 640;
                adsRequest.nonLinearAdSlotHeight = 150;
                // Request video ads
                this.adsLoader.requestAds(adsRequest);
            });
        });
    }
    resetSetup() {
        this.playerIsPlaying = false;
        this.videoIsStarted = false;
        this.currentTime = 0;
        window.clearInterval(this.progressTimer);
    }
    onAdsManagerLoaded(event) {
        const adsRenderingSettings = new google.ima.AdsRenderingSettings();
        adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
        this.player = event.getAdsManager(this.videoPlaceholder, adsRenderingSettings);
        this.player.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, this.onVastError.bind(this));
        this.player.addEventListener(google.ima.AdEvent.Type.COMPLETE, this.closeVideo.bind(this));
        // get duration
        this.player.addEventListener(google.ima.AdEvent.Type.AD_PROGRESS, (e) => {
            if (!this.duration) {
                this.duration = e.getAdData().duration;
            }
        });
        this.player.init(640, 360, google.ima.ViewMode.NORMAL);
        this.mapPlaybackMethods();
        this.fitAdToBox();
    }
    closeVideo() {
        this.resetSetup();
        this.player.destroy();
        this.adsLoader.destroy();
        // empty out video content
        while (this.videoPlaceholder.firstChild) {
            this.videoPlaceholder.removeChild(this.videoPlaceholder.firstChild);
        }
        this.pluginSetup().then(() => {
            // ensure volume is still muted when autoplay looping (no user interaction trigger)
            // otherwise, autoplay will fail to loop.
            this.setVolume(this.autoPlayInProgress ? 0 : this.getVolume());
            this.end();
        });
    }
    /**
     * Map playback methods
     */
    mapPlaybackMethods() {
        if (this.methodBounds) {
            return;
        }
        // avoid binding when re-init
        this.methodBounds = true;
        this.isPlaying = () => this.playerIsPlaying;
        // Note: Standard level is 0 -> 100. but this api is 0 -> 1
        this.getVolume = () => this.player.getVolume() * 100;
        this.setVolume = (level) => this.player.setVolume(level / 100);
        this.isMuted = () => this.getVolume() === 0;
        this.getCurrentTime = () => this.currentTime;
        this.seekTo = (_second) => {
            debug('no seek function available for IMA video');
        };
        this.pluginPause = () => {
            this.player.pause();
            this.playerIsPlaying = false;
            window.clearInterval(this.progressTimer);
        };
        this.pluginPlay = () => {
            this.playerIsPlaying = true;
            this.monitorProgress();
            if (this.videoIsStarted) {
                this.player.resume();
                return;
            }
            this.player.start();
            this.videoIsStarted = true;
        };
        this.pluginMute = () => this.setVolume(0);
        this.pluginUnMute = () => this.setVolume(100);
    }
    onVastError(e) {
        debug(e.getError());
        this.player.destroy();
    }
    // ensure vast ad content fit within the box
    fitAdToBox() {
        const adContent = this.videoElement;
        adContent.style.height = '100%';
        const vastAdContent = adContent.firstChild;
        vastAdContent.style.width = '100%';
        vastAdContent.style.height = '100%';
        // actual video element that plays the video
        // set its video as cover or contain
        const video = adContent.querySelector('video');
        if (video) {
            video.style.objectFit = this.config.cover ? 'cover' : '';
        }
    }
    // IMA video doesn't give currentTime, we need to monitor the progress
    monitorProgress() {
        if (this.progressTimer) {
            window.clearInterval(this.progressTimer);
        }
        this.progressTimer = window.setInterval(() => {
            if (this.currentTime < this.duration) {
                const nextTick = this.currentTime + 0.1;
                this.currentTime =
                    nextTick > this.duration ? this.duration : nextTick;
            }
        }, 100);
    }
}
export default VideoIMA;
export const preSetupIMA = () => {
    // already set up previously. no need to do further
    if (window.IMALoadPromise) {
        return window.IMALoadPromise;
    }
    window.IMALoadPromise = new Promise(resolve => {
        loadScriptTag('https://imasdk.googleapis.com/js/sdkloader/ima3.js').then(() => {
            resolve();
        });
    });
    return window.IMALoadPromise;
};
